import { useState } from "react";
import { FilterOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Divider, Flex, Popover, Row, Tag, Typography } from "antd";
import { border, radius, shadow, spacing, typography } from "../../theme/tokens";

const { Text } = Typography;

const DEFAULT_SPAN = { xs: 24, sm: 12, md: 8, lg: 6 };

/**
 * Dải công cụ chuẩn của mọi màn danh sách CMS (band 3 của khung 4 dải:
 * PageHeader → StatStrip → ListToolbar → CmsTableCard).
 *
 * Trái:  Bộ lọc · Cột hiển thị · Làm mới · tag bộ lọc đang bật · Xoá tất cả
 * Phải:  slot `actions` (Thêm mới / Xuất Excel / hành động hàng loạt...)
 * Dưới:  ô lọc chỉ hiện khi được bật, nằm trong CÙNG thẻ, ngăn bằng Divider.
 *
 * Tách ra từ `CustomerToolbar` sau khi 3-4 màn copy nguyên khối ~120 dòng giống
 * nhau (chỉ khác danh sách filter/cột) — mỗi bản copy lại lệch một chi tiết.
 *
 * QUAN TRỌNG — `onHideFilter`: ẩn một ô lọc thì phải xoá luôn GIÁ TRỊ của nó.
 * Các bản cũ chỉ bỏ key khỏi danh sách hiển thị nên điều kiện lọc vẫn còn hiệu
 * lực trong khi không còn ô nào trên màn, người dùng không hiểu vì sao bảng thiếu
 * dữ liệu.
 *
 * @param {Array}  filterDefs      [{ key, label, span? }] — thứ tự hiển thị
 * @param {object} filters         { [key]: ReactNode } control tương ứng từng key
 * @param {func}   onHideFilter    (key) => void — xoá giá trị của 1 bộ lọc
 * @param {func}   onClearFilters  () => void — xoá giá trị của tất cả bộ lọc
 * @param {Array}  columnDefs      [{ key, label, fixed? }] — bỏ trống là ẩn nút "Cột hiển thị"
 * @param {Array}  visibleColumns  key các cột đang hiện
 * @param {func}   setVisibleColumns
 * @param {Array}  defaultVisibleColumns  dùng cho "Đặt lại mặc định"
 * @param {node}   actions         nhóm nút bên phải
 * @param {node}   extraLeft       nút phụ chèn cuối nhóm trái (vd: chú thích)
 */
export default function ListToolbar({
  filterDefs = [],
  filters = {},
  onHideFilter,
  onClearFilters,
  defaultVisibleFilters = [],
  columnDefs,
  visibleColumns = [],
  setVisibleColumns,
  defaultVisibleColumns,
  loading = false,
  onReload,
  actions,
  extraLeft,
}) {
  const [visibleFilters, setVisibleFilters] = useState(defaultVisibleFilters);

  const toggleableColumns = (columnDefs || []).filter((c) => !c.fixed);
  const showColumnPicker = Boolean(setVisibleColumns) && toggleableColumns.length > 0;

  const hideFilter = (key) => {
    setVisibleFilters((prev) => prev.filter((k) => k !== key));
    onHideFilter?.(key);
  };

  const clearAll = () => {
    setVisibleFilters([]);
    onClearFilters?.();
  };

  const filterPicker = (
    <div style={{ width: 220 }}>
      <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, borderBottom: border.divider }}>
        <Text strong style={{ fontSize: typography.size.sm }}>
          Chọn bộ lọc hiển thị
        </Text>
      </div>
      <div style={{ padding: spacing[2] }}>
        {filterDefs.map((f) => (
          <div key={f.key} style={{ padding: `${spacing[1]}px 0` }}>
            <Checkbox
              checked={visibleFilters.includes(f.key)}
              onChange={(e) => {
                if (e.target.checked) setVisibleFilters((prev) => [...prev, f.key]);
                else hideFilter(f.key);
              }}
            >
              {f.label}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );

  const columnPicker = (
    <div style={{ width: 240 }}>
      <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, borderBottom: border.divider }}>
        <Text strong style={{ fontSize: typography.size.sm }}>
          Chọn cột hiển thị
        </Text>
      </div>
      <div style={{ padding: spacing[2], maxHeight: 320, overflowY: "auto" }}>
        {toggleableColumns.map((col) => (
          <div key={col.key} style={{ padding: `${spacing[1]}px 0` }}>
            <Checkbox
              checked={visibleColumns.includes(col.key)}
              onChange={(e) =>
                setVisibleColumns((prev) =>
                  e.target.checked ? [...prev, col.key] : prev.filter((k) => k !== col.key)
                )
              }
            >
              {col.label}
            </Checkbox>
          </div>
        ))}
      </div>
      {defaultVisibleColumns && (
        <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, borderTop: border.divider }}>
          <Button
            type="link"
            size="small"
            onClick={() => setVisibleColumns(defaultVisibleColumns)}
            style={{ padding: 0, fontSize: typography.size.sm }}
          >
            Đặt lại mặc định
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card
      size="small"
      style={{
        marginBottom: spacing[4],
        borderRadius: radius.md,
        border: border.hairline,
        boxShadow: shadow.xs,
      }}
      styles={{ body: { padding: spacing[4] } }}
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap={spacing[3]}>
        <Flex gap={spacing[2]} align="center" wrap="wrap">
          {filterDefs.length > 0 && (
            <Popover content={filterPicker} trigger="click" placement="bottomLeft">
              <Button icon={<FilterOutlined />}>
                Bộ lọc {visibleFilters.length > 0 && `(${visibleFilters.length})`}
              </Button>
            </Popover>
          )}

          {showColumnPicker && (
            <Popover content={columnPicker} trigger="click" placement="bottomLeft">
              <Button icon={<SettingOutlined />}>Cột hiển thị</Button>
            </Popover>
          )}

          {onReload && (
            <Button icon={<ReloadOutlined />} loading={loading} onClick={onReload}>
              Làm mới
            </Button>
          )}

          {visibleFilters.map((key) => {
            const def = filterDefs.find((f) => f.key === key);
            return def ? (
              <Tag
                key={key}
                closable
                onClose={() => hideFilter(key)}
                style={{ borderRadius: radius.pill }}
              >
                {def.label}
              </Tag>
            ) : null;
          })}

          {visibleFilters.length > 0 && (
            <Button
              type="link"
              size="small"
              danger
              onClick={clearAll}
              style={{ fontSize: typography.size.sm }}
            >
              Xoá tất cả
            </Button>
          )}

          {extraLeft}
        </Flex>

        {actions && (
          <Flex gap={spacing[2]} wrap="wrap">
            {actions}
          </Flex>
        )}
      </Flex>

      {visibleFilters.length > 0 && (
        <>
          <Divider style={{ margin: `${spacing[3]}px 0` }} />
          <Row gutter={[spacing[3], spacing[3]]}>
            {filterDefs
              .filter((f) => visibleFilters.includes(f.key) && filters[f.key])
              .map((f) => (
                <Col key={f.key} {...(f.span || DEFAULT_SPAN)}>
                  {filters[f.key]}
                </Col>
              ))}
          </Row>
        </>
      )}
    </Card>
  );
}
